'use client';

import { IcCloseCircle, IconButton, TextField, Typography } from '@musinsa/mds';
import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useCuratorSnackbar } from '../../../common/hooks';
import { isContentUrlValidationError } from '../../utils/contentUrlValidationError';
import { isValidContentUrl, normalizeContentUrl } from '../../utils/normalizeContentUrl';
import { SeedingFloatingActionBar } from '../seedingDetail/common/SeedingFloatingActionBar';

/** 채널×콘텐츠 유형 불일치(COMMON-001-002) 시 콘텐츠 URL 인풋에 노출할 인라인 에러 문구. */
const CHANNEL_TYPE_MISMATCH_MESSAGE = '콘텐츠 채널과 유형에 맞는 URL을 입력해 주세요.';

interface ContentSubmissionGuide {
  title: string;
  items: readonly string[];
}

interface ContentSubmissionPageViewProps {
  /**
   * 표시용 채널 라벨 (예: "인스타그램"). 채널은 입력 URL host 로 특정하므로 보통 미지정 —
   * 미지정 시 placeholder 를 채널명 없이 generic 으로 노출.
   */
  channelLabel?: string;
  /** 콘텐츠 유형 라벨 (예: "이미지", "숏폼 영상") */
  contentTypeLabel: string;
  /** 기존 제출 URL — null 이면 edit mode 진입, string 이면 view mode 진입 */
  submittedUrl: string | null;
  /** 가이드 — PRD §2-6/§3-4 기반 안내 N행 */
  guide: ContentSubmissionGuide;
  /** URL 등록/수정 — 제출(mutation)을 수행하고, 성공 시 resolve·실패 시 reject 한다. (SID-4284) */
  onSubmit: (url: string) => Promise<void>;
  /** 진입 직후 Snackbar 강제 노출 (검수용) */
  initialShowSnackbar?: boolean;
  className?: string;
}

/**
 * 콘텐츠 링크 제출 페이지 — PRD §3-1 "링크 수정 가능" / §3-4 "수정 가능 / 삭제 불가".
 *
 * 단일 페이지 안에서 `edit` ↔ `view` 모드 토글 (Figma 366:92959/92945/93002):
 * - 미제출 진입 → edit (콘텐츠 URL TextField + "완료", URL 유효 시 활성화)
 * - 제출 완료 진입 → view (콘텐츠 URL underline 링크 + "수정하기")
 * - 등록/수정 완료 직후 → view + Snackbar (useCuratorSnackbar 사용)
 *
 * view 모드에서 URL 링크 클릭 시 해당 게시물로 아웃링크. CTA 는 기존 시딩 페이지와 동일하게
 * SeedingFloatingActionBar(BottomButtonWrapper) 패턴 사용.
 */
export const ContentSubmissionPageView = ({
  channelLabel,
  contentTypeLabel,
  submittedUrl,
  guide,
  onSubmit,
  initialShowSnackbar = false,
  className,
}: ContentSubmissionPageViewProps) => {
  const snackbar = useCuratorSnackbar();
  const [mode, setMode] = useState<'edit' | 'view'>(submittedUrl ? 'view' : 'edit');
  const [url, setUrl] = useState(submittedUrl ?? '');
  // 채널×유형 불일치(COMMON-001-002) 인라인 에러. 입력 변경 시 해제. (정산 입력과 동일 컨셉)
  const [urlError, setUrlError] = useState<string | null>(null);

  // SID-4170: 스킴(https://)을 생략한 입력도 허용한다. 검증은 정규화 후 도메인 형식으로 판단.
  const canSubmit = isValidContentUrl(url);

  // 검수용 진입 즉시 Snackbar (mount 시 한 번)
  useEffect(() => {
    if (initialShowSnackbar) {
      snackbar.show({ message: '콘텐츠 URL을 등록했습니다.' });
    }
    // mount 시 한 번만. snackbar 객체는 안정적이라 deps 에 포함해도 무한 루프 없으나
    // 검수용 1회 노출이 의도라 빈 deps 유지.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const wasEditingExisting = mode === 'view' || submittedUrl !== null;
    // SID-4170: 제출 시점에 스킴을 정규화(https:// prepend)해 절대 URL 로 저장한다.
    const normalizedUrl = normalizeContentUrl(url);
    setUrlError(null);
    try {
      // SID-4284: 제출이 성공한 경우에만 view(수정하기) 전이·성공 스낵바를 노출한다. 기존엔 mutation
      // 결과와 무관하게 즉시 전이해, 실패해도 버튼이 '수정하기'(제출완료)로 바뀌고 성공/실패 토스트가 공존했다.
      await onSubmit(normalizedUrl);
      setUrl(normalizedUrl);
      setMode('view');
      snackbar.show({
        message: wasEditingExisting ? '콘텐츠 URL을 수정했습니다.' : '콘텐츠 URL을 등록했습니다.',
      });
    } catch (error) {
      // 제출 실패 — edit 모드 유지. 채널/유형 불일치(클라 사전검증 + 서버 COMMON-001-002)는
      // page 가 ContentUrlValidationError 로 통일해 던지므로 인풋 인라인 에러로 노출,
      // 그 외 실패 토스트는 onSubmit(호출 측 page)이 노출한다. (SID-4284 / QAT 검수)
      if (isContentUrlValidationError(error)) {
        setUrlError(CHANNEL_TYPE_MISMATCH_MESSAGE);
      }
    }
  };

  const handleEnterEdit = () => {
    setMode('edit');
    setUrlError(null);
  };

  return (
    // SeedingCampaignDetailLayout 과 동일 구조 — flex-1 외곽 + grow 본문이 남은 높이를 채워
    // sticky CTA 를 하단에 고정. (콘텐츠 짧을 때 CTA 가 본문 바로 아래 붙던 문제)
    <div className={twMerge('flex flex-col flex-1 bg-white', className)}>
      <div className="grow">
        {/* 콘텐츠 URL — edit: TextField / view: underline 아웃링크 (figma 366:92945/93002) */}
        <div className="flex flex-col px-[16px]! pt-[12px]! pb-[8px]! gap-[8px]!">
          <Typography variant="body14pxMed" colorScheme="black">
            콘텐츠 URL
          </Typography>
          {mode === 'edit' ? (
            // 입력값이 있으면 우측에 clear(×) 아이콘 노출 (figma 366:92945)
            <div className="relative">
              <TextField
                placeholder={
                  channelLabel
                    ? `${channelLabel} ${contentTypeLabel} 링크를 입력해주세요`
                    : `${contentTypeLabel} 링크를 입력해주세요`
                }
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  // 입력을 고치면 인라인 에러 해제 (정산 입력과 동일).
                  if (urlError) setUrlError(null);
                }}
                // 채널×유형 불일치(COMMON-001-002) 시 빨간 border. help text 는 하단 Typography.
                error={!!urlError}
                fullWidth
                className={url ? 'pr-[32px]!' : undefined}
              />
              {url && (
                <IconButton
                  onClick={() => {
                    setUrl('');
                    setUrlError(null);
                  }}
                  aria-label="입력 삭제"
                  size="size20x20"
                  className="absolute top-1/2! right-[8px] -translate-y-1/2!"
                >
                  <IcCloseCircle fillColor="gray-400" />
                </IconButton>
              )}
            </div>
          ) : (
            // 링크 선택 시 해당 게시물로 아웃링크. 스킴 정규화된 절대 URL 사용. (figma flow)
            <a
              href={normalizeContentUrl(url)}
              target="_blank"
              rel="noreferrer"
              className="break-all underline"
            >
              {/* 수정된 링크 타이포 — gray-600 body13reg + underline (피드백) */}
              <Typography variant="body13pxReg" colorScheme="gray-600" className="break-all">
                {url}
              </Typography>
            </a>
          )}
          {/* 채널×유형 불일치(COMMON-001-002) 인라인 에러 — 빨간 텍스트(빨간 border 는 TextField error). */}
          {mode === 'edit' && urlError && (
            <Typography variant="body13pxReg" className="text-red-500">
              {urlError}
            </Typography>
          )}
        </div>

        {/* 유의 사항 — 타이틀↔ul 8px, li↔li 8px (figma) */}
        <div className="flex flex-col px-[16px]! pt-[12px]! gap-[8px]!">
          <Typography variant="body14pxMed" colorScheme="black">
            {guide.title}
          </Typography>
          {guide.items.map((item) => (
            <div key={item} className="flex gap-[4px]! items-start">
              <Typography variant="body13pxReg" colorScheme="gray-600">
                ·
              </Typography>
              <Typography variant="body13pxReg" colorScheme="gray-600">
                {item}
              </Typography>
            </div>
          ))}
        </div>
      </div>

      {mode === 'edit' ? (
        <SeedingFloatingActionBar
          mode="single"
          primary={{ label: '완료', onClick: handleSubmit, disabled: !canSubmit }}
        />
      ) : (
        <SeedingFloatingActionBar
          mode="single"
          primary={{ label: '수정하기', onClick: handleEnterEdit }}
        />
      )}
    </div>
  );
};
